import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useInView,
} from "framer-motion";

// ─── Image imports — replace with actual paths ─────────────────────────
import heroImg from "../assets/about.jpeg";
import img1 from "../assets/about.jpeg";
import img2 from "../assets/about.jpeg";
import img3 from "../assets/about.jpeg";
import img4 from "../assets/about.jpeg";

// ─── Global ────────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@200;300;400&display=swap');
  *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
  :root{
    --cream:#F7F3EE; --cream-dark:#EDE7DC; --warm-tan:#C9AA8C;
    --terracotta:#B87355; --mocha:#7A5C47; --ink:#2E1F14;
    --ink-soft:#5C4033; --gold:#C49A6C;
    --font-serif:'Cormorant Garamond',Georgia,serif;
    --font-sans:'Jost',sans-serif;
  }
  html{scroll-behavior:smooth;}
  body{
    background:var(--cream); color:var(--ink);
    font-family:var(--font-sans); font-weight:300;
    -webkit-font-smoothing:antialiased; overflow-x:hidden;
  }
`;

// ─── Marquee ───────────────────────────────────────────────────────────
const marqAnim = keyframes`
  from{transform:translateX(0);}
  to{transform:translateX(-50%);}
`;

// ─── Styled ────────────────────────────────────────────────────────────
const Page = styled.div`
  background: var(--cream);
  overflow: hidden;
`;

// ── 1. HERO — full-bleed image, name reveals over it ──────────────────
const HeroSection = styled.div`
  position: relative;
  height: 100vh;
  overflow: hidden;
  background: var(--ink);
`;

const HeroImg = styled(motion.div)`
  position: absolute;
  inset: -10% 0;
  background-image: url(${heroImg});
  background-size: cover;
  background-position: center 20%;
  will-change: transform;
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    170deg,
    rgba(14, 7, 4, 0.55) 0%,
    rgba(14, 7, 4, 0.25) 45%,
    rgba(14, 7, 4, 0.65) 100%
  );
`;

const HeroContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 5vw 5vw 8vh;
`;

const HeroEyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.8rem;
`;
const EDash = styled(motion.span)`
  display: block;
  width: 36px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.9;
`;
const ELabel = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--terracotta);
`;

const HWrap = styled.div`
  overflow: hidden;
  margin-bottom: 0.1rem;
`;
const HeroName = styled(motion.h1)`
  font-family: var(--font-serif);
  font-size: clamp(5rem, 13vw, 14rem);
  font-weight: 300;
  line-height: 0.82;
  color: var(--cream);
  letter-spacing: -0.03em;
`;
const HeroNameItalic = styled(HeroName)`
  font-style: italic;
  color: var(--warm-tan);
`;

const HeroTagline = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: clamp(0.75rem, 1vw, 0.88rem);
  font-weight: 200;
  letter-spacing: 0.1em;
  line-height: 1.8;
  color: rgba(247, 243, 238, 0.55);
  margin-top: 2rem;
  max-width: 40ch;
`;

const ScrollHint = styled(motion.div)`
  position: absolute;
  bottom: 2.5rem;
  right: 5vw;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-family: var(--font-sans);
  font-size: 0.55rem;
  font-weight: 300;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: rgba(247, 243, 238, 0.25);
`;
const ScanLine = styled.div`
  width: 30px;
  height: 1px;
  background: rgba(247, 243, 238, 0.15);
  overflow: hidden;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--terracotta);
    animation: scan 1.8s ease-in-out infinite;
  }
  @keyframes scan {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

// ── 2. MARQUEE ────────────────────────────────────────────────────────
const MarqueeTrack = styled.div`
  background: var(--ink);
  padding: 1.1rem 0;
  overflow: hidden;
  border-bottom: 1px solid rgba(247, 243, 238, 0.06);
`;
const MarqueeInner = styled.div`
  display: flex;
  width: max-content;
  animation: ${marqAnim} 26s linear infinite;
`;
const MItem = styled.span`
  font-family: var(--font-serif);
  font-size: 0.85rem;
  font-weight: 300;
  font-style: italic;
  color: rgba(247, 243, 238, 0.2);
  padding: 0 2.4rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 2.4rem;
  &::after {
    content: "✦";
    font-style: normal;
    font-size: 0.45rem;
    color: var(--terracotta);
    opacity: 0.6;
  }
`;
const TICKER = [
  "Make-up & Hair",
  "Bridal Specialist",
  "München",
  "Natürliche Ausstrahlung",
  "Shootings",
  "Workshops",
  "1:1 Coaching",
  "Mit Herz dabei",
];

// ── 3. INTRO — sticky image left, text scrolls right ─────────────────
const IntroSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 140vh;
  background: var(--cream);
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const IntroImgCol = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  align-self: flex-start;
  @media (max-width: 800px) {
    position: relative;
    height: 70vw;
  }
`;
const IntroImg = styled(motion.div)`
  position: absolute;
  inset: -10% 0;
  background-image: url(${img1});
  background-size: cover;
  background-position: center 15%;
`;
const IntroImgFade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 60%, var(--cream) 100%);
`;

const IntroTextCol = styled.div`
  padding: 12vh 7vw 12vh 5vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (max-width: 800px) {
    padding: 5rem 6vw;
  }
`;

const SectionEyebrow = ({ children, inView }) => {
  const ref = useRef(null);
  const iv = useInView(ref, { once: true, margin: "-10%" });
  const show = inView !== undefined ? inView : iv;
  return (
    <div
      ref={ref}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        marginBottom: "1.4rem",
      }}
    >
      <motion.span
        style={{
          display: "block",
          width: "32px",
          height: "1px",
          background: "var(--terracotta)",
          opacity: 0.8,
          transformOrigin: "left",
        }}
        initial={{ scaleX: 0 }}
        animate={show ? { scaleX: 1 } : {}}
        transition={{ duration: 0.6 }}
      />
      <motion.span
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "0.6rem",
          fontWeight: 300,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          color: "var(--terracotta)",
        }}
        initial={{ opacity: 0, x: -8 }}
        animate={show ? { opacity: 1, x: 0 } : {}}
        transition={{ delay: 0.15, duration: 0.6 }}
      >
        {children}
      </motion.span>
    </div>
  );
};

const BigHeadWrap = styled.div`
  overflow: hidden;
  margin-bottom: 0.1rem;
`;
const BigHead = styled(motion.h2)`
  font-family: var(--font-serif);
  font-size: clamp(2.8rem, 4.5vw, 5rem);
  font-weight: 300;
  line-height: 0.88;
  color: var(--ink);
  letter-spacing: -0.02em;
`;
const BigHeadItalic = styled(BigHead)`
  font-style: italic;
  color: var(--terracotta);
`;

function FadeUp({ children, delay = 0, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });
  return (
    <motion.div
      ref={ref}
      style={style}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function RevealLine({ children, delay = 0, as: Tag = motion.h2, ...props }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });
  return (
    <BigHeadWrap ref={ref}>
      <Tag
        initial={{ y: "105%" }}
        animate={inView ? { y: "0%" } : {}}
        transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
        {...props}
      >
        {children}
      </Tag>
    </BigHeadWrap>
  );
}

const BodyText = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: clamp(0.82rem, 1.1vw, 0.95rem);
  font-weight: 200;
  letter-spacing: 0.05em;
  line-height: 1.95;
  color: var(--ink-soft);
  margin-bottom: 1.8rem;
  max-width: 42ch;
`;

const SignatureLine = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-top: 1rem;
`;
const SigRule = styled(motion.div)`
  width: 36px;
  height: 1px;
  background: var(--gold);
  opacity: 0.5;
`;
const SigName = styled.span`
  font-family: var(--font-serif);
  font-size: 1.2rem;
  font-weight: 300;
  font-style: italic;
  color: var(--warm-tan);
`;

// ── 4. DARK QUOTE SECTION ─────────────────────────────────────────────
const QuoteSection = styled.div`
  background: var(--ink);
  padding: 10rem 5vw;
  position: relative;
  overflow: hidden;
`;
const QuoteGrain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
`;
const QuoteInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;
const QuoteDash = styled(motion.div)`
  width: 40px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.6;
  margin: 0 auto 2.5rem;
`;
const QuoteLine = ({ children, delay, inView }) => {
  const ref = useRef(null);
  const iv = useInView(ref, { once: true, margin: "-10%" });
  return (
    <div ref={ref} style={{ overflow: "hidden", marginBottom: "0.1rem" }}>
      <motion.h2
        style={{
          fontFamily: "var(--font-serif)",
          fontSize: "clamp(1.8rem,3.5vw,4rem)",
          fontWeight: 300,
          fontStyle: "italic",
          lineHeight: 1.2,
          color: "var(--cream)",
          letterSpacing: "-0.01em",
        }}
        initial={{ y: "105%" }}
        animate={inView || iv ? { y: "0%" } : {}}
        transition={{
          duration: 0.9,
          delay: delay || 0,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        {children}
      </motion.h2>
    </div>
  );
};
const QuoteAttrib = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(247, 243, 238, 0.22);
  display: block;
  margin-top: 2.5rem;
`;

// ── 5. TWO-IMAGE SPLIT ────────────────────────────────────────────────
const SplitSection = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  min-height: 90vh;
  background: var(--cream-dark);
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;
const SplitLeft = styled.div`
  position: relative;
  overflow: hidden;
  min-height: 60vh;
`;
const SplitImg = styled(motion.div)`
  position: absolute;
  inset: -10% 0;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
`;
const SplitRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(3rem, 6vw, 8rem) clamp(2.5rem, 5vw, 6rem);
`;

// ── 6. VALUES GRID ────────────────────────────────────────────────────
const ValuesSection = styled.div`
  background: var(--cream);
  padding: 10rem 5vw;
`;
const ValuesHeader = styled.div`
  max-width: 1200px;
  margin: 0 auto 5rem;
`;
const ValuesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: rgba(122, 92, 71, 0.1);
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;
const ValueCell = styled(motion.div)`
  background: var(--cream);
  padding: 3rem 2.5rem;
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--terracotta);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  }
  &:hover::before {
    transform: scaleX(1);
  }
`;
const ValueIcon = styled.div`
  font-size: 1.1rem;
  color: var(--terracotta);
  opacity: 0.6;
  margin-bottom: 1.4rem;
`;
const ValueTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: clamp(1.3rem, 1.8vw, 1.6rem);
  font-weight: 300;
  color: var(--ink);
  margin-bottom: 0.8rem;
  line-height: 1.1;
`;
const ValueDesc = styled.p`
  font-family: var(--font-sans);
  font-size: 0.75rem;
  font-weight: 200;
  letter-spacing: 0.05em;
  line-height: 1.85;
  color: var(--ink-soft);
`;

const VALUES = [
  {
    icon: "◈",
    title: "Natürlichkeit",
    desc: "Ich arbeite nicht gegen dich, sondern mit dir. Jeder Look hebt hervor, was schon da ist — authentisch und abgestimmt auf dich.",
  },
  {
    icon: "◇",
    title: "Ruhe & Vertrauen",
    desc: "Besonders am Hochzeitstag schaffst du mit mir eine Atmosphäre voller Ruhe. Du sollst dich sicher und getragen fühlen.",
  },
  {
    icon: "○",
    title: "Präzision im Detail",
    desc: "Von der perfekten Haltbarkeit bis zu den kleinen Finessen, die den Unterschied machen — ich achte auf alles, was zählt.",
  },
];

// ── 7. EXPERIENCE NUMBERS ─────────────────────────────────────────────
const NumbersBand = styled.div`
  background: var(--ink);
  padding: 7rem 5vw;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const NumCell = styled(motion.div)`
  padding: 2rem 2.5rem;
  border-left: 1px solid rgba(247, 243, 238, 0.07);
  &:first-child {
    border-left: none;
  }
  @media (max-width: 700px) {
    &:nth-child(3) {
      border-left: none;
    }
    border-top: 1px solid rgba(247, 243, 238, 0.07);
    &:nth-child(1),
    &:nth-child(2) {
      border-top: none;
    }
  }
`;
const NumBig = styled.div`
  font-family: var(--font-serif);
  font-size: clamp(3rem, 5vw, 5.5rem);
  font-weight: 300;
  color: var(--cream);
  line-height: 1;
  letter-spacing: -0.03em;
  margin-bottom: 0.5rem;
  display: inline-block;
  padding-bottom: 0.08em;
`;
const NumSuffix = styled.span`
  font-style: italic;
  color: var(--terracotta);
  font-size: 0.7em;
`;
const NumLabel = styled.div`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(247, 243, 238, 0.3);
  margin-bottom: 0.4rem;
`;
const NumSub = styled.div`
  font-family: var(--font-sans);
  font-size: 0.65rem;
  font-weight: 200;
  letter-spacing: 0.05em;
  line-height: 1.6;
  color: rgba(247, 243, 238, 0.18);
`;

// ── 8. GALLERY STRIP ─────────────────────────────────────────────────
const GalleryTrack = styled.div`
  height: 55vh;
  overflow: hidden;
  background: var(--cream-dark);
  position: relative;
`;
const GalleryRail = styled(motion.div)`
  display: flex;
  gap: 8px;
  height: 100%;
  width: max-content;
  padding: 0 5vw;
  will-change: transform;
`;
const GalleryFrame = styled.div`
  height: 100%;
  flex-shrink: 0;
  overflow: hidden;
  aspect-ratio: ${({ $portrait }) => ($portrait ? "2/3" : "3/2")};
  background: var(--cream-dark);
  position: relative;
`;
const GalleryImgInner = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
`;
const GALLERY = [
  { src: img1, portrait: true },
  { src: img2, portrait: false },
  { src: img3, portrait: true },
  { src: img4, portrait: false },
  { src: img1, portrait: false },
  { src: img2, portrait: true },
  { src: img3, portrait: false },
];

// ── 9. CTA ────────────────────────────────────────────────────────────
const CtaSection = styled.div`
  background: var(--cream-dark);
  padding: 10rem 5vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2.5rem;
`;
const CtaRule = styled(motion.div)`
  width: 48px;
  height: 1px;
  background: linear-gradient(90deg, var(--terracotta), var(--gold));
  opacity: 0.5;
`;
const CtaHead = styled(motion.p)`
  font-family: var(--font-serif);
  font-size: clamp(1.8rem, 3.5vw, 3.5rem);
  font-weight: 300;
  font-style: italic;
  color: var(--ink);
  line-height: 1.2;
`;
const CtaSub = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.8rem;
  font-weight: 200;
  letter-spacing: 0.07em;
  line-height: 1.85;
  color: var(--ink-soft);
  max-width: 38ch;
`;
const CtaBtns = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;
const CtaBtn = styled(motion.a)`
  font-family: var(--font-sans);
  font-size: 0.68rem;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 1.1rem 2.8rem;
  background: ${({ $primary }) => ($primary ? "var(--ink)" : "transparent")};
  color: ${({ $primary }) => ($primary ? "var(--cream)" : "var(--ink)")};
  border: 1px solid
    ${({ $primary }) => ($primary ? "var(--ink)" : "rgba(46,31,20,0.2)")};
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: ${({ $primary }) =>
      $primary ? "var(--terracotta)" : "rgba(46,31,20,0.05)"};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
    z-index: 0;
  }
  &:hover::after {
    transform: scaleX(1);
  }
  span {
    position: relative;
    z-index: 1;
  }
`;

// ─── Main ──────────────────────────────────────────────────────────────
export default function AboutPage() {
  // ── Hero parallax ──
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const rawHeroY = useTransform(heroScroll, [0, 1], ["-8%", "8%"]);
  const heroImgY = useSpring(rawHeroY, { stiffness: 50, damping: 18 });
  const heroTextY = useTransform(heroScroll, [0, 1], ["0%", "28%"]);
  const heroOpacity = useTransform(heroScroll, [0, 0.6], [1, 0]);
  const hintOpacity = useTransform(heroScroll, [0, 0.15], [1, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "About Me - Laureta Martini";
  }, []);

  // ── Intro image parallax ──
  const introRef = useRef(null);
  const { scrollYProgress: introScroll } = useScroll({
    target: introRef,
    offset: ["start end", "end start"],
  });
  const rawIntroY = useTransform(introScroll, [0, 1], ["-10%", "10%"]);
  const introImgY = useSpring(rawIntroY, { stiffness: 50, damping: 18 });

  // ── Split section parallax ──
  const splitRef = useRef(null);
  const { scrollYProgress: splitScroll } = useScroll({
    target: splitRef,
    offset: ["start end", "end start"],
  });
  const rawSplitY = useTransform(splitScroll, [0, 1], ["-8%", "8%"]);
  const splitImgY = useSpring(rawSplitY, { stiffness: 50, damping: 18 });

  // ── Gallery horizontal scroll ──
  const galleryRef = useRef(null);
  const { scrollYProgress: gallScroll } = useScroll({
    target: galleryRef,
    offset: ["start end", "end start"],
  });
  const rawGallX = useTransform(gallScroll, [0, 1], ["0%", "-22%"]);
  const gallX = useSpring(rawGallX, { stiffness: 40, damping: 16 });

  // ── Section inView refs ──
  const quoteRef = useRef(null);
  const numRef = useRef(null);
  const ctaRef = useRef(null);
  const valRef = useRef(null);
  const splitTRef = useRef(null);
  const quoteInView = useInView(quoteRef, { once: true, margin: "-10%" });
  const numInView = useInView(numRef, { once: true, margin: "-10%" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-10%" });
  const valInView = useInView(valRef, { once: true, margin: "-8%" });
  const splitTInView = useInView(splitTRef, { once: true, margin: "-10%" });

  return (
    <>
      <GlobalStyle />
      <Page>
        {/* ══ 1. HERO ══ */}
        <HeroSection ref={heroRef}>
          <HeroImg style={{ y: heroImgY }} />
          <HeroGradient />
          <HeroContent>
            <motion.div style={{ y: heroTextY, opacity: heroOpacity }}>
              <HeroEyebrowRow>
                <EDash
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  style={{ transformOrigin: "left" }}
                />
                <ELabel
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.55, duration: 0.6 }}
                >
                  Make-up & Hair · München
                </ELabel>
              </HeroEyebrowRow>

              <HWrap>
                <HeroName
                  initial={{ y: "105%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 0.9,
                    delay: 0.3,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  Laureta
                </HeroName>
              </HWrap>
              <HWrap>
                <HeroNameItalic
                  as={motion.h1}
                  initial={{ y: "105%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 0.9,
                    delay: 0.42,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  Styling.
                </HeroNameItalic>
              </HWrap>

              <HeroTagline
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75, duration: 0.9 }}
              >
                Make-up & Hairstylistin mit einem klaren Fokus — Looks, in denen
                du dich wirklich wie du selbst fühlst.
              </HeroTagline>
            </motion.div>
          </HeroContent>

          <ScrollHint style={{ opacity: hintOpacity }}>
            <ScanLine />
            Scroll
          </ScrollHint>
        </HeroSection>

        {/* ══ 2. MARQUEE ══ */}
        <MarqueeTrack>
          <MarqueeInner>
            {[...TICKER, ...TICKER, ...TICKER, ...TICKER].map((t, i) => (
              <MItem key={i}>{t}</MItem>
            ))}
          </MarqueeInner>
        </MarqueeTrack>

        {/* ══ 3. INTRO — sticky image + scrolling text ══ */}
        <IntroSection ref={introRef}>
          <IntroImgCol>
            <IntroImg style={{ y: introImgY }} />
            <IntroImgFade />
          </IntroImgCol>

          <IntroTextCol>
            <FadeUp>
              <SectionEyebrow>Über mich</SectionEyebrow>
            </FadeUp>

            <RevealLine as={BigHead} delay={0.05}>
              Hinter dem
            </RevealLine>
            <RevealLine as={BigHeadItalic} delay={0.18}>
              Pinsel.
            </RevealLine>

            <div style={{ marginTop: "2.5rem" }}>
              <FadeUp delay={0.08}>
                <BodyText>
                  Ich bin Laureta — Make-up & Hairstylistin mit einem klaren
                  Fokus: Looks zu kreieren, in denen du dich wirklich wie du
                  selbst fühlen kannst.
                </BodyText>
              </FadeUp>
              <FadeUp delay={0.14}>
                <BodyText>
                  Für mich geht es nicht darum, jemanden zu verändern, sondern
                  die natürliche Ausstrahlung gezielt zu unterstreichen. Jeder
                  Look entsteht individuell — abgestimmt auf deine Gesichtszüge,
                  deinen Stil und den Anlass.
                </BodyText>
              </FadeUp>
              <FadeUp delay={0.2}>
                <BodyText style={{ marginBottom: 0 }}>
                  Ich liebe es, Frauen dabei zu begleiten, sich an besonderen
                  Tagen selbstbewusst und schön zu fühlen.
                </BodyText>
              </FadeUp>
            </div>

            <FadeUp delay={0.1} style={{ marginTop: "2.5rem" }}>
              <SignatureLine>
                <SigRule
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  style={{ transformOrigin: "left" }}
                />
                <SigName>— Laureta</SigName>
              </SignatureLine>
            </FadeUp>
          </IntroTextCol>
        </IntroSection>

        {/* ══ 4. QUOTE ══ */}
        <QuoteSection ref={quoteRef}>
          <QuoteGrain />
          <QuoteInner>
            <QuoteDash
              initial={{ scaleX: 0 }}
              animate={quoteInView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.7 }}
            />
            <QuoteLine delay={0.1} inView={quoteInView}>
              „Nicht verändern —
            </QuoteLine>
            <QuoteLine delay={0.22} inView={quoteInView}>
              unterstreichen, was schon da ist."
            </QuoteLine>
            <QuoteAttrib
              initial={{ opacity: 0 }}
              animate={quoteInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              — Laureta, Make-up & Hairstylistin
            </QuoteAttrib>
          </QuoteInner>
        </QuoteSection>

        {/* ══ 5. SPLIT — image + bridal text ══ */}
        <SplitSection ref={splitRef}>
          <SplitLeft>
            <SplitImg $src={img2} style={{ y: splitImgY }} />
          </SplitLeft>
          <SplitRight ref={splitTRef}>
            <FadeUp>
              <SectionEyebrow>Bridal</SectionEyebrow>
            </FadeUp>

            <RevealLine as={BigHead} delay={0.05}>
              Ruhe &amp;
            </RevealLine>
            <RevealLine as={BigHeadItalic} delay={0.17}>
              Vertrauen.
            </RevealLine>

            <div style={{ marginTop: "2.2rem" }}>
              <FadeUp delay={0.1}>
                <BodyText>
                  Besonders im Bridal Bereich ist mir eines wichtig: Ruhe,
                  Vertrauen und ein Gefühl von Sicherheit. Dein Styling soll
                  nicht nur gut aussehen — es soll sich auch genau richtig
                  anfühlen.
                </BodyText>
              </FadeUp>
              <FadeUp delay={0.18}>
                <BodyText style={{ marginBottom: 0 }}>
                  Mit meiner Erfahrung aus zahlreichen Hochzeiten weiß ich,
                  worauf es ankommt — von der perfekten Haltbarkeit bis zu den
                  Details, die den Unterschied machen.
                </BodyText>
              </FadeUp>
            </div>
          </SplitRight>
        </SplitSection>

        {/* ══ 6. VALUES ══ */}
        <ValuesSection>
          <ValuesHeader>
            <FadeUp>
              <SectionEyebrow>Was mich antreibt</SectionEyebrow>
            </FadeUp>
            <RevealLine as={BigHead} delay={0.08}>
              Meine
            </RevealLine>
            <RevealLine as={BigHeadItalic} delay={0.2}>
              Werte.
            </RevealLine>
          </ValuesHeader>

          <ValuesGrid ref={valRef}>
            {VALUES.map((v, i) => (
              <ValueCell
                key={v.title}
                initial={{ opacity: 0, y: 28 }}
                animate={valInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  delay: i * 0.12,
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <ValueIcon>{v.icon}</ValueIcon>
                <ValueTitle>{v.title}</ValueTitle>
                <ValueDesc>{v.desc}</ValueDesc>
              </ValueCell>
            ))}
          </ValuesGrid>
        </ValuesSection>

        {/* ══ 7. NUMBERS ══ */}
        <NumbersBand ref={numRef}>
          {[
            {
              num: "240+",
              label: "Kundinen",
              sub: "Jedes Gesicht, eine Geschichte",
            },
            { num: "8+", label: "Jahre Erfahrung", sub: "Seit 2016 aktiv" },
            { num: "100+", label: "Hochzeiten", sub: "Unvergessliche Momente" },
            {
              num: "98%",
              label: "Wiederbuchungsrate",
              sub: "Kunden, die immer kommen",
            },
          ].map((n, i) => (
            <NumCell
              key={n.label}
              initial={{ opacity: 0, y: 20 }}
              animate={numInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                delay: i * 0.1,
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <NumBig>{n.num}</NumBig>
              <NumLabel>{n.label}</NumLabel>
              <NumSub>{n.sub}</NumSub>
            </NumCell>
          ))}
        </NumbersBand>

        {/* ══ 8. GALLERY STRIP ══ */}
        <GalleryTrack ref={galleryRef}>
          <GalleryRail style={{ x: gallX }}>
            {GALLERY.map((g, i) => (
              <GalleryFrame key={i} $portrait={g.portrait}>
                <GalleryImgInner $src={g.src} />
              </GalleryFrame>
            ))}
          </GalleryRail>
        </GalleryTrack>

        {/* ══ 9. CTA ══ */}
        <CtaSection ref={ctaRef}>
          <CtaRule
            initial={{ scaleX: 0 }}
            animate={ctaInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.7 }}
          />
          <CtaHead
            initial={{ opacity: 0, y: 16 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.8 }}
          >
            Bereit, gemeinsam etwas zu schaffen?
          </CtaHead>
          <CtaSub
            initial={{ opacity: 0, y: 12 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.8 }}
          >
            Ob du ein Datum im Kopf hast oder einfach ein gutes Gefühl suchst —
            melde dich und lass uns reden.
          </CtaSub>
          <CtaBtns
            initial={{ opacity: 0, y: 10 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.38, duration: 0.7 }}
          >
            <CtaBtn
              $primary
              href="https://wa.me/4915000000000"
              target="_blank"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <span>WhatsApp schreiben</span>
            </CtaBtn>
            <CtaBtn
              href="/portfolio"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <span>Portfolio ansehen</span>
            </CtaBtn>
          </CtaBtns>
        </CtaSection>
      </Page>
    </>
  );
}
